import { Object3D, Vector3 } from "three";
import { SignalConfig } from "../../config/SignalConfig";
import { Gutter } from "../../scene/extras/gutter/Gutter";
import { SlopeTrim } from "./SlopeTrim";
import { CornerBoxTrim } from "./CornerBoxTrim";
import { EaveTrim } from "./EaveTrim";
import { IRoofSideTrimMetrics } from "./IRoofSideTrimMetrics";
import { Main } from "../../Main";
import { Building } from "../Building";
import { ObjectUtils } from "../../utils/data/ObjectUtils";

export interface IRoofSideTrimConfig
{
	parent: Object3D;
	pitch:SignalConfig<number>;
	side?: string; // "left" or "right"
	isLeanto?: boolean;
	downSpoutPadding?: number;
}

/**
 * Represents the trim of one side of a sloped roof.
 * 2 of these is needed on the building, and one per each lean to roof.
 * It uses 2 slope trims, and either
 *  - 1 eave trim and 2 corner boxes
 *  - or 1 gutter
 */
export class RoofSideTrim
{
	public static SIDE_LEFT  = "left";
	public static SIDE_RIGHT = "right";

	private _config     : IRoofSideTrimConfig;

	private _parent     : Object3D;
	private _pitch      : SignalConfig<number>;
	private _gutter     : Gutter;
	private _gutterEnabled : boolean = false;

	private _container  : Object3D;

	private _frontSlope : SlopeTrim;
	private _backSlope  : SlopeTrim;

	private _frontBox   : CornerBoxTrim;
	private _backBox    : CornerBoxTrim;

	private _sideEave   : EaveTrim;

	private _temp       : Vector3;

	private _metrics : IRoofSideTrimMetrics;
	private _isFrontBoxOkToShow : boolean;
	private _isBackBoxOkToShow : boolean;

	constructor(config:IRoofSideTrimConfig)
	{
		this._config = config;

		this._parent = config.parent;
		this._pitch  = config.pitch;
		this._isFrontBoxOkToShow = true;
		this._isBackBoxOkToShow = true;

		this._container = new Object3D();
		this._temp = new Vector3();

		this.init();

		this._gutter = new Gutter(this._container);
		this.updateGutterEnabled();
	}

	private onGutterEnabledChange = (value:boolean) =>
	{
		this.updateGutterEnabled();
	};

	private updateGutterEnabled()
	{
		// if gutter is enabled -> hide eave, otherwise show

		var gutterEnabled = Main.getInstance().config.building.gutter.value;

		var gutterEnabledBySide = true;

		var single_slope = Main.getInstance().config.building.single_slope.value;
		if (single_slope && single_slope && this._config.side === Building.WALLS.RIGHT)
		{
			gutterEnabledBySide = false;
		}

		if (gutterEnabled && this._gutterEnabled && gutterEnabledBySide)
		{
			this._sideEave.hide();
			if (this._isFrontBoxOkToShow)
			{
				this._frontBox.show();
			}
			if (this._isBackBoxOkToShow)
			{
				this._backBox.show();
			}
			this._gutter.setEnabled(true);

			if (this._metrics && (this._config.isLeanto == null || this._config.isLeanto === false))
			{
				if (Main.getInstance().config.building.leanto[this._metrics.side].enabled.value === true)
				{
					this._gutter.setEnabled(false);
					if (Main.getInstance().config.building.leanto[this._metrics.side].drop.value === 0 && ((!Main.getInstance().config.building.single_slope.value && this._metrics.side) || this._metrics.side === "left"))
					{
						this._frontBox.hide();
						this._backBox.hide();
					}
				}
			}
		}
		else
		{
			this._sideEave.show();
			if (this._isFrontBoxOkToShow)
			{
				this._frontBox.show();
			}
			if (this._isBackBoxOkToShow)
			{
				this._backBox.show();
			}
			this._gutter.setEnabled(false);

			if (this._metrics && (this._config.isLeanto == null || this._config.isLeanto === false))
			{
				if (Main.getInstance().config.building.leanto[this._metrics.side].enabled.value === true)
				{
					if (Main.getInstance().config.building.leanto[this._metrics.side].drop.value === 0 && ((!Main.getInstance().config.building.single_slope.value && this._metrics.side) || this._metrics.side === "left"))
					{
						this._sideEave.hide();
						this._frontBox.hide();
						this._backBox.hide();
					}
				}
			}
		}
	}

	private init = () =>
	{
		// SlopeTrim

		this._frontSlope = new SlopeTrim({
			parent: this._container,
			rotation:0,
			flip: true
		});

		this._backSlope = new SlopeTrim({
			parent: this._container,
			rotation: 0,
			flip: false
		});

		// EaveTrim

		this._sideEave = new EaveTrim(this._container, this._pitch);

		// CornerBoxTrim

		this._frontBox = new CornerBoxTrim({
			parent: this._container,
			flip: true
		});

		this._backBox = new CornerBoxTrim({
			parent: this._container,
			flip: false
		});

		// add container

		this._parent.add(this._container);
	};

	public update(metrics:IRoofSideTrimMetrics)
	{
		var side_offset = 0.45;

		var alpha = Math.atan(metrics.pitch/12);

		var side_offset_x = side_offset * Math.cos(alpha);
		var side_offset_y = side_offset * Math.sin(alpha);

		// need to keep original metrics intact
		metrics = ObjectUtils.clone(metrics);
		this._metrics  = metrics;
		metrics.x     -= side_offset_x;
		metrics.top   -= side_offset_y;
		metrics.width += side_offset_x;

		var pitch = metrics.pitch;
		var length = metrics.z_front - metrics.z_back;

		// make the edge of the trim stand out a bit

		this._temp.set(metrics.x, metrics.top, 0);
		var position = this._temp;

		// end

		// front, backSlope

		var frontSlopePosition = new Vector3(0, 0, metrics.z_front);
		frontSlopePosition.addVectors(position, frontSlopePosition);

		var backSlopePosition = new Vector3(0, 0, metrics.z_back);
		backSlopePosition.addVectors(position, backSlopePosition);

		if (metrics.wraparound_front)
		{
			this._frontSlope.hide();
			this._frontBox.update(frontSlopePosition, 0.3); // trial and error alignment value
		}
		else
		{
			this._frontSlope.show();
			this._frontSlope.update(frontSlopePosition, metrics.width, metrics.pitch, true);
			this._frontBox.update(frontSlopePosition, pitch);
		}

		if (metrics.wraparound_back)
		{
			this._backSlope.hide();
			this._backBox.update(backSlopePosition, 0.3);
		}
		else
		{
			this._backSlope.show();
			this._backSlope.update(backSlopePosition, metrics.width, metrics.pitch, true);
			this._backBox.update(backSlopePosition, pitch);
		}


		if (this._config.isLeanto)
		{
			var leantoConfig = Main.getInstance().config.building.leanto[metrics.side];
			var cutFront = metrics.side === Building.WALLS.RIGHT ? leantoConfig.cut_back.value : leantoConfig.cut_front.value;
			var cutBack  = metrics.side === Building.WALLS.RIGHT ? leantoConfig.cut_front.value : leantoConfig.cut_back.value;
			var widthFront = cutFront === 0 && leantoConfig.drop.value === 0 ? metrics.width*1.02 : metrics.width;
			var widthBack  = cutBack  === 0 && leantoConfig.drop.value === 0 ? metrics.width*1.02 : metrics.width;
			this._frontSlope.update(frontSlopePosition, widthFront, metrics.pitch, true);
			this._backSlope.update(backSlopePosition, widthBack, metrics.pitch, true);
		}


		// sideEave

		this._sideEave.update(backSlopePosition, length, pitch);


		if (Main.getInstance().config.building.leanto[metrics.side].enabled.value === true)
		{
			if (Main.getInstance().config.building.leanto[metrics.side].drop.value === 0)
			{
				this._sideEave.hide();
			}
		}
		else
		{
			if (!this._gutterEnabled)
			{
				this._sideEave.show();
			}
		}


		// end

		// insetBay

		if (metrics.insetBay)
		{
			if (metrics.side === "left")
			{
				if (metrics.insetBay.front.count > 0)// && metrics.insetBay.front.drop === 0)
				{
					this._frontSlope.hide();
					this._frontBox.hide();
					this._isFrontBoxOkToShow = false;
				}
				else
				{
					this._frontSlope.show();
					this._frontBox.show();
					this._isFrontBoxOkToShow = true;
				}

				if (metrics.insetBay.back.count > 0)// && metrics.insetBay.back.drop === 0)
				{
					this._backSlope.hide();
					this._backBox.hide();
					this._isBackBoxOkToShow = false;
				}
				else
				{
					this._backSlope.show();
					this._backBox.show();
					this._isBackBoxOkToShow = true;
				}
			}
			else if (metrics.side)
			{
				if (metrics.insetBay.front.count > 0)// && metrics.insetBay.front.drop === 0)
				{
					this._backSlope.hide();
					this._backBox.hide();
					this._isBackBoxOkToShow = false;
				}
				else
				{
					this._backSlope.show();
					this._backBox.show();
					this._isBackBoxOkToShow = true;
				}

				if (metrics.insetBay.back.count > 0)// && metrics.insetBay.back.drop === 0)
				{
					this._frontSlope.hide();
					this._frontBox.hide();
					this._isFrontBoxOkToShow = false;
				}
				else
				{
					this._frontSlope.show();
					this._frontBox.show();
					this._isFrontBoxOkToShow = true;
				}
			}
		}

		// end

		// gutter

		this._gutter.updateSize(metrics);

		this._gutterEnabled = metrics.gutter_enabled;
		this.updateGutterEnabled();
	}

	public get container()
	{
		return this._container;
	}
}
